import { WILDCARD_SERVICE_NAME, SERVICE_TYPES, type PermissionLevel } from "@/utils/constants";

export function mapAccessLevelToLevel(accessLevel: string): PermissionLevel {
    const lower = accessLevel.toLowerCase();
    if (lower.includes("write") || lower === "w") {
        return "w";
    }
    if (lower.includes("admin") || accessLevel === "*") {
        return "*";
    }
    return "r";
}

export function parseSingleService(service: string): { name: string; level: PermissionLevel } {
    const raw = (service || "").trim();
    if (!raw) {
        return { name: WILDCARD_SERVICE_NAME, level: "r" };
    }

    const parts = raw.split(".");
    const rawName = (parts[0] || WILDCARD_SERVICE_NAME).toLowerCase();

    // Normalize any wildcard-ish value ("all" or "*") to the canonical wildcard name
    const name =
        rawName === "all" || rawName === "*" ? WILDCARD_SERVICE_NAME : rawName;

    const rawLevel = parts[1] || "r";
    const level = mapAccessLevelToLevel(rawLevel);

    return { name, level };
}

export function parseServiceFromRequest(service: string | string[]): { name: string; level: PermissionLevel }[] {
    const services = Array.isArray(service) ? service : [service];
    return services.map(parseSingleService);
}

export function formatDisplayName(
    businessName: string,
    teamName: string,
    serviceName: string
): string {
    const business = businessName === "*" ? "All Business" : businessName;
    const team = teamName === "*" ? "All Team" : teamName;
    const service =
        serviceName === "*"
            ? "All"
            : SERVICE_TYPES.find((s) => s.value === serviceName.toLowerCase())?.label ?? serviceName;
    return `${business} - ${team} - ${service}`;
}

export function matchesAccessFilter(
    businessName: string,
    teamName: string,
    serviceName: string,
    query: string,
    options?: { matchBusiness?: boolean; requireTeamOrServiceMatch?: boolean }
): boolean {
    const raw = (query || "").trim();
    if (!raw) return true;
    const terms = raw.toLowerCase().split(/\s+/).filter((t) => t.length > 0);
    if (terms.length === 0) return true;
    const business = (businessName || "").toLowerCase();
    const team = (teamName || "").toLowerCase();
    const service = (serviceName || "").toLowerCase();
    const matchBusiness = options?.matchBusiness !== false;
    const allTermsMatch = terms.every((term) => {
        return (
            (matchBusiness && business.includes(term)) ||
            team.includes(term) ||
            service.includes(term)
        );
    });
    if (!allTermsMatch) return false;
    if (options?.requireTeamOrServiceMatch) {
        const atLeastOneTermMatchesTeamOrService = terms.some(
            (term) => team.includes(term) || service.includes(term)
        );
        return atLeastOneTermMatchesTeamOrService;
    }
    return true;
}
